import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlayCircle, RefreshCw } from 'lucide-react';
import { StandaloneUnitDto } from '../../types/unit';
import { unitBrowseService } from '../../services/unitBrowseService';
import { unitCodeService } from '../../services/unitCodeService';
import { cardTheme } from '../../styles/cardTheme';
import StandaloneUnitCard from './StandaloneUnitCard';

interface StandaloneUnitsSectionProps {
  teacherId?: string;
  title?: string;
  subtitle?: string;
  className?: string;
  pageSize?: number;
}

const StandaloneUnitsSection: React.FC<StandaloneUnitsSectionProps> = ({
  teacherId,
  title = 'الحصص المستقلة',
  subtitle = 'حصص يمكن شراؤها مباشرة بدون اشتراك في كورس كامل',
  className = 'py-8 sm:py-12',
  pageSize = 12,
}) => {
  const [units, setUnits] = useState<StandaloneUnitDto[]>([]);
  const [enrolledIds, setEnrolledIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadEnrolledIds = useCallback(async () => {
    try {
      const response = await unitCodeService.getMyUnitCodes(1, 100);
      if (response.success && response.data?.units) {
        setEnrolledIds(new Set(response.data.units.map((u) => u.unitId)));
      }
    } catch {
      // Non-blocking — browse still works without enrolled state
    }
  }, []);

  const loadUnits = useCallback(
    async (page: number) => {
      setIsLoading(true);
      setError(null);
      const result = await unitBrowseService.getUnitsWithoutCourse({
        teacherId,
        pageNumber: page,
        pageSize,
      });

      if (result.success) {
        setUnits(result.data.data.filter((u) => u.active));
        setTotalPages(result.data.totalPages);
        setTotalCount(result.data.totalCount);
        setCurrentPage(result.data.currentPage);
      } else {
        setUnits([]);
        setError(result.error);
      }
      setIsLoading(false);
    },
    [teacherId, pageSize]
  );

  useEffect(() => {
    loadUnits(1);
    loadEnrolledIds();
  }, [loadUnits, loadEnrolledIds]);

  const handleRefresh = () => {
    loadUnits(currentPage);
    loadEnrolledIds();
  };

  const shimmer = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, i) => (
        <div
          key={`shimmer-${i}`}
          className="bg-white rounded-2xl border border-green-100 overflow-hidden animate-pulse"
        >
          <div className="aspect-[16/10] bg-gray-200" />
          <div className="p-4 space-y-3">
            <div className="h-5 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-100 rounded w-1/2" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
        </div>
      )),
    []
  );

  if (!isLoading && !error && units.length === 0) {
    return null;
  }

  return (
    <section className={className}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <div className={`inline-flex items-center gap-2 mb-3 ${cardTheme.badge}`}>
              <PlayCircle className="w-4 h-4" />
              حصص
            </div>
            <h2 className={`text-xl sm:text-2xl lg:text-3xl font-bold mb-2 ${cardTheme.title}`}>
              {title}
            </h2>
            <p className={`text-sm sm:text-base max-w-2xl ${cardTheme.subtitle}`}>{subtitle}</p>
            {!isLoading && totalCount > 0 && (
              <p className="text-sm text-gray-500 mt-2">
                {totalCount} {totalCount === 1 ? 'حصة' : 'حصص'} متاحة
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoading}
            className="self-start sm:self-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-green-200 text-green-700 hover:bg-green-50 text-sm font-semibold min-h-11 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            تحديث
          </button>
        </div>

        {error && !isLoading ? (
          <div className="text-center py-10 rounded-2xl bg-gray-50 border border-gray-100">
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={handleRefresh}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold ${cardTheme.btnPrimary}`}
            >
              إعادة المحاولة
            </button>
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {shimmer}
          </div>
        ) : (
          <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {units.map((unit) => (
              <StandaloneUnitCard
                key={unit.id}
                unit={unit}
                isEnrolled={enrolledIds.has(unit.id)}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && !isLoading && !error && (
          <div className="flex justify-center items-center gap-2 mt-8">
            <button
              type="button"
              onClick={() => loadUnits(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-4 py-2 rounded-lg bg-white text-gray-600 hover:bg-green-50 border border-gray-200 disabled:opacity-50 min-h-11"
            >
              السابق
            </button>
            <span className="text-sm text-gray-600 px-2">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => loadUnits(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-4 py-2 rounded-lg bg-white text-gray-600 hover:bg-green-50 border border-gray-200 disabled:opacity-50 min-h-11"
            >
              التالي
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default StandaloneUnitsSection;
